import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { TargetAssignmentsKpi } from "@/actions/ssr/target/getTargetAssignmentsKpiSSR";
import type { TargetAssignmentItem } from "@/actions/ssr/target/getTargetAssignmentsSSR";
import type {
  DeptPerformanceChart,
  DeptPerformanceItem,
} from "@/actions/ssr/target/getDeptPerformanceChartSSR";
import type { TaskStatusChart } from "@/actions/ssr/target/getTaskStatusChartSSR";
import type { LeaderboardResponse } from "@/actions/ssr/target/getLeaderboardSSR";

interface TypeCounts {
  sales_target: number;
  visit: number;
  general: number;
  collection: number;
}

interface TargetState {
  kpi: TargetAssignmentsKpi;
  chart: TaskStatusChart;
  deptChart: DeptPerformanceItem[];
  /** §22 rejected the window as over-cap — `deptChart` is empty for that reason. */
  deptChartRangeTooWide: boolean;
  assignments: TargetAssignmentItem[];
  count: number;
  total_pages: number;
  current_page: number;
  typeCounts: TypeCounts;
  leaderboard: LeaderboardResponse;
  targetDateFilter: { from: string | null; to: string | null };
  assignmentsClientFetched: boolean;
  kpiClientFetched: boolean;
  chartClientFetched: boolean;
  deptChartClientFetched: boolean;
  leaderboardClientFetched: boolean;
}

const initialState: TargetState = {
  kpi: {
    total_assigned: 0,
    total_assigned_trend_pct: 0,
    active_targets: 0,
    active_near_completion: 0,
    completion_rate_pct: 0,
    completion_rate_trend_pct: 0,
    pending_reviews: 0,
    total_completed: 0,
  },
  chart: { completed: 0, ongoing: 0, pending: 0 },
  deptChart: [],
  deptChartRangeTooWide: false,
  assignments: [],
  count: 0,
  total_pages: 1,
  current_page: 1,
  leaderboard: {
    count: 0,
    next: null,
    previous: null,
    total_pages: 1,
    current_page: 1,
    results: [],
  },
  typeCounts: { sales_target: 0, visit: 0, general: 0, collection: 0 },
  targetDateFilter: { from: null, to: null },
  assignmentsClientFetched: false,
  kpiClientFetched: false,
  chartClientFetched: false,
  deptChartClientFetched: false,
  leaderboardClientFetched: false,
};

const targetSlice = createSlice({
  name: "target",
  initialState,
  reducers: {
    setTargetKpi(state, action: PayloadAction<TargetAssignmentsKpi>) {
      state.kpiClientFetched = true;
      state.kpi = action.payload;
    },
    setTargetKpiFromSSR(state, action: PayloadAction<TargetAssignmentsKpi>) {
      if (state.kpiClientFetched) return;
      state.kpi = action.payload;
    },
    setTargetChart(state, action: PayloadAction<TaskStatusChart>) {
      state.chartClientFetched = true;
      state.chart = action.payload;
    },
    setTargetChartFromSSR(state, action: PayloadAction<TaskStatusChart>) {
      if (state.chartClientFetched) return;
      state.chart = action.payload;
    },
    // Payload is the whole §22 response, not just `departments`, so the
    // `range_too_wide` flag survives into the store — an empty array otherwise
    // looks identical to "no departments have targets".
    setTargetDeptChart(state, action: PayloadAction<DeptPerformanceChart>) {
      state.deptChartClientFetched = true;
      state.deptChart = action.payload.departments;
      state.deptChartRangeTooWide = action.payload.range_too_wide ?? false;
    },
    setTargetDeptChartFromSSR(
      state,
      action: PayloadAction<DeptPerformanceChart>,
    ) {
      if (state.deptChartClientFetched) return;
      state.deptChart = action.payload.departments;
      state.deptChartRangeTooWide = action.payload.range_too_wide ?? false;
    },
    setTargetAssignments(
      state,
      action: PayloadAction<{
        results: TargetAssignmentItem[];
        count: number;
        total_pages: number;
        current_page: number;
        sales_target_count: number;
        visit_count: number;
        general_count: number;
        collection_count: number;
      }>,
    ) {
      state.assignmentsClientFetched = true;
      state.assignments = action.payload.results;
      state.count = action.payload.count;
      state.total_pages = action.payload.total_pages;
      state.current_page = action.payload.current_page;
      state.typeCounts = {
        sales_target: action.payload.sales_target_count,
        visit: action.payload.visit_count,
        general: action.payload.general_count,
        collection: action.payload.collection_count,
      };
    },
    setTargetAssignmentsFromSSR(
      state,
      action: PayloadAction<{
        results: TargetAssignmentItem[];
        count: number;
        total_pages: number;
        current_page: number;
        sales_target_count: number;
        visit_count: number;
        general_count: number;
        collection_count: number;
      }>,
    ) {
      if (state.assignmentsClientFetched) return;
      state.assignments = action.payload.results;
      state.count = action.payload.count;
      state.total_pages = action.payload.total_pages;
      state.current_page = action.payload.current_page;
      state.typeCounts = {
        sales_target: action.payload.sales_target_count,
        visit: action.payload.visit_count,
        general: action.payload.general_count,
        collection: action.payload.collection_count,
      };
    },
    setLeaderboard(state, action: PayloadAction<LeaderboardResponse>) {
      state.leaderboardClientFetched = true;
      state.leaderboard = action.payload;
    },
    setLeaderboardFromSSR(state, action: PayloadAction<LeaderboardResponse>) {
      if (state.leaderboardClientFetched) return;
      state.leaderboard = action.payload;
    },
    setTargetDateFilter(
      state,
      action: PayloadAction<{ from: string | null; to: string | null }>,
    ) {
      state.targetDateFilter = action.payload;
    },
    patchTargetAssignment(
      state,
      action: PayloadAction<{ id: string; status: string; progress: number }>,
    ) {
      const row = state.assignments.find((a) => a.id === action.payload.id);
      if (row) {
        row.status = action.payload.status as TargetAssignmentItem["status"];
        row.progress = action.payload.progress;
      }
    },
  },
});

export const {
  setTargetKpi,
  setTargetKpiFromSSR,
  setTargetChart,
  setTargetChartFromSSR,
  setTargetDeptChart,
  setTargetDeptChartFromSSR,
  setTargetAssignments,
  setTargetAssignmentsFromSSR,
  setLeaderboard,
  setLeaderboardFromSSR,
  setTargetDateFilter,
  patchTargetAssignment,
} = targetSlice.actions;
export default targetSlice.reducer;
